<style lang="less" scoped>
@import "/lib/Colors.less";
.modal-content {
    width: min(90vw, 600px);
}
.address-group {
    gap: 1.25em;

    .address-options {
        flex-grow: 1;
        padding: 1em 1em 0.75em;
        border: 1px solid @bs-table-border-color;
    }
}
</style>
<template>
    <div class="modal-content" ref="container">
        <div class="modal-header">
            <h2 class="modal-title">Address Verification</h2>
            <button v-if="type == AddressVerificationEnum.VerifyReFormat" type="button" class="close icon icon-close" aria-hidden="true" @click="$emit('close')" autofocus aria-label="Close" />
        </div>
        <div class="modal-body">
            <p describedby-text v-if="type == 1">We are not able to verify your address, but found a close match.</p>
            <p describedby-text v-else-if="type == 2">Address entered has been verified and updated.</p>
            <div class="row no-gutters address-group">
                <div class="address-options">
                    <p class="mb-0">{{ userEnteredAddress.Address1 }}</p>
                    <p class="mb-0" v-if="userEnteredAddress.Address2">{{ userEnteredAddress.Address2 }}</p>
                    <p class="mb-0">
                        <span>{{ userEnteredAddress.City }}</span>
                        <span>,&nbsp;</span>
                        <span>{{ userEnteredAddress.State }}</span>
                        <span>&nbsp;</span>
                        <span>{{ userEnteredAddress.Zip }}</span>
                    </p>
                    <p class="mb-0" v-if="userEnteredAddress.Country">{{ userEnteredAddress.Country }}</p>
                    <button type="button" id="useAsEntered" class="btn btn-outline-secondary mt-3 w-100" v-if="type == 1" @click.prevent="$emit('close')">Use as entered</button>
                </div>
                <div class="address-options">
                    <p class="mb-0" :class="{ 'font-weight-bold': !isEqual(suggestedAddress.Address1, 'Address1') }">{{ suggestedAddress.Address1 }}</p>
                    <p class="mb-0" :class="{ 'font-weight-bold': !isEqual(suggestedAddress.Address2, 'Address2') }" v-if="suggestedAddress.Address2">{{ suggestedAddress.Address2 }}</p>
                    <p class="mb-0">
                        <span :class="{ 'font-weight-bold': !isEqual(suggestedAddress.City, 'City') }">
                            {{ suggestedAddress.City }}
                        </span>
                        <span>,&nbsp;</span>
                        <span :class="{ 'font-weight-bold': !isEqual(suggestedAddress.State, 'State') }">
                            {{ suggestedAddress.State }}
                        </span>
                        <span>&nbsp;</span>
                        <span :class="{ 'font-weight-bold': !isEqual(suggestedAddress.ZipCode, 'Zip') }">
                            {{ suggestedAddress.ZipCode }}
                        </span>
                    </p>
                    <p class="mb-0" :class="{ 'font-weight-bold': !isEqual(suggestedAddress.Country, 'Country') }" v-if="suggestedAddress.Country">{{ suggestedAddress.Country }}</p>
                    <button type="button" id="useSuggested" class="btn btn-primary mt-3 w-100" v-if="type == 1" @click.prevent="populateSuggested">Use suggested</button>
                </div>
            </div>
        </div>
    </div>
</template>
<script lang="ts">
import { computed, defineComponent, ref, SetupContext } from "vue";
import { dialogSetup, DialogProps } from "@/lib/dialogMixin.3";
import AddressDef, { VerifiedAddress } from "../elements/AddressDef";
import { AddressVerificationEnum } from "@cs-ts/address-verification-enum";
import { usePageStore } from "../pageState/store";
import { useMainStore } from "@/mainStore";
import { KnownFeatureFlags } from "@cs/KnownFeatureFlags";
type VerifiedAddressExtended = VerifiedAddress & { Zip?: string };
type Props = {
    data: AddressDef;
    userEnteredAddress: VerifiedAddressExtended;
    suggestedAddress: VerifiedAddressExtended;
    populateSuggestedAddress: (suggested: VerifiedAddressExtended) => void;
} & DialogProps;
export default defineComponent({
    props: ["data", "userEnteredAddress", "suggestedAddress", "populateSuggestedAddress", "triggerElement"],
    setup(props: Props, { emit }: SetupContext) {
        const containerRef = ref<HTMLElement>(null!);
        dialogSetup(containerRef);
        const mainStore = useMainStore();
        const pageStore = usePageStore();

        const type = computed(() => props.data.definition.settings.verifyAddress);

        const populateSuggested = async () => {
            props.populateSuggestedAddress(props.suggestedAddress);
            emit("close");
        };
        const isEqual = (suggestedData: "string", prop: keyof VerifiedAddressExtended) => {
            return suggestedData == props.userEnteredAddress[prop];
        };

        (async () => {
            if (await mainStore.isFeatureFlagActiveAsync(KnownFeatureFlags.AbortStaleRequests.Id)) {
                const abortController = pageStore.abortControllers.get(props.data.vmId);
                abortController?.controller.signal.addEventListener(
                    "abort",
                    () => {
                        emit("close");
                    },
                    { once: true },
                );
            }
        })();

        return {
            AddressVerificationEnum,
            container: containerRef,
            type,
            isEqual,
            populateSuggested,
        };
    },
});
</script>
